import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { InternetPlan } from '../types';
import {
  buildCheckServiceUrl,
  checkServiceAtCoords,
  clearLeadContext,
  LeadContext,
  newPlacesSessionToken,
  placeAutocomplete,
  placeDetails,
  PlacePrediction,
  saveLeadContext,
} from '../utils/leadContext';
import CoverageCheckAnimation, { withMinCheckDuration } from './CoverageCheckAnimation';

type VerifiedPlace = {
  formatted: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  lat: number;
  lng: number;
};

type Props = {
  open: boolean;
  plan: InternetPlan | null;
  onClose: () => void;
};

const PlanConnectModal: React.FC<Props> = ({ open, plan, onClose }) => {
  const navigate = useNavigate();
  const [address, setAddress] = useState('');
  const [suggestions, setSuggestions] = useState<PlacePrediction[]>([]);
  const [listOpen, setListOpen] = useState(false);
  const [loadingSuggest, setLoadingSuggest] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const [status, setStatus] = useState<'idle' | 'checking' | 'error' | 'unavailable'>('idle');
  const [message, setMessage] = useState('');

  const sessionRef = useRef(newPlacesSessionToken());
  const debounceRef = useRef<number | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const skipSuggestRef = useRef(false);
  const checkSeqRef = useRef(0);
  const suggestSeqRef = useRef(0);
  const goTimerRef = useRef<number | null>(null);

  const bumpCheck = () => {
    checkSeqRef.current += 1;
    if (goTimerRef.current != null) {
      window.clearTimeout(goTimerRef.current);
      goTimerRef.current = null;
    }
    return checkSeqRef.current;
  };

  useEffect(() => {
    if (!open) return;
    bumpCheck();
    clearLeadContext();
    setAddress('');
    setSuggestions([]);
    setListOpen(false);
    setStatus('idle');
    setMessage('');
    setHighlight(-1);
    sessionRef.current = newPlacesSessionToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setListOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    if (skipSuggestRef.current) {
      skipSuggestRef.current = false;
      return;
    }
    const q = address.trim();
    if (q.length < 3) {
      setSuggestions([]);
      setListOpen(false);
      return;
    }
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    const seq = ++suggestSeqRef.current;
    debounceRef.current = window.setTimeout(async () => {
      setLoadingSuggest(true);
      try {
        const preds = await placeAutocomplete(q, sessionRef.current);
        if (seq !== suggestSeqRef.current) return;
        setSuggestions(preds);
        setListOpen(preds.length > 0);
        setHighlight(-1);
      } catch (_) {
        if (seq !== suggestSeqRef.current) return;
        setSuggestions([]);
        setListOpen(false);
      } finally {
        if (seq === suggestSeqRef.current) setLoadingSuggest(false);
      }
    }, 220);
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [address, open]);

  if (!open || !plan) return null;

  const goToContact = (ctx: LeadContext) => {
    const url = buildCheckServiceUrl({
      plan: { id: plan.id, name: plan.name, price: plan.price, speed: plan.speed },
      lead: ctx,
    });
    onClose();
    navigate(url, {
      state: {
        selectedPlan: plan.id,
        selectedPlanName: plan.name,
        selectedPlanPrice: plan.price,
        selectedPlanSpeed: plan.speed,
        leadContext: ctx,
      },
    });
    window.scrollTo(0, 0);
  };

  const continueWithPlace = async (place: VerifiedPlace, existingSeq?: number) => {
    const seq = existingSeq ?? bumpCheck();
    clearLeadContext();
    setStatus('checking');
    setMessage('');
    setSuggestions([]);
    setListOpen(false);
    try {
      const result = await withMinCheckDuration(
        checkServiceAtCoords(place.formatted || place.address, place.lat, place.lng, {
          fullLos: false,
        }),
        2600
      );
      if (seq !== checkSeqRef.current) return;

      const available = !!(result.available || result.canConnect) && !result.losBlocked;
      const ctx: LeadContext = {
        address: place.address || place.formatted,
        city: place.city || 'Kingman',
        state: place.state || 'AZ',
        zipCode: place.zipCode || '',
        lat: place.lat,
        lng: place.lng,
        formatted: place.formatted,
        available,
        tower: result.tower,
        technology: result.technology,
        checkedAt: new Date().toISOString(),
      };

      if (!available) {
        clearLeadContext();
        setStatus('unavailable');
        setMessage(
          result.losBlocked
            ? 'No service available — line of sight to our towers is blocked by terrain.'
            : 'No service available at this address.'
        );
        return;
      }

      saveLeadContext(ctx);
      setStatus('idle');
      setMessage('Service available — continuing…');
      goTimerRef.current = window.setTimeout(() => {
        if (seq !== checkSeqRef.current) return;
        goToContact(ctx);
      }, 700);
    } catch (err: any) {
      if (seq !== checkSeqRef.current) return;
      clearLeadContext();
      setStatus('error');
      setMessage(err?.message || 'Something went wrong. Please try again.');
    }
  };

  const pickSuggestion = async (pred: PlacePrediction) => {
    const seq = bumpCheck();
    skipSuggestRef.current = true;
    setAddress(pred.description);
    setSuggestions([]);
    setListOpen(false);
    setStatus('checking');
    setMessage('');
    try {
      const details = await placeDetails(pred.placeId, sessionRef.current);
      if (seq !== checkSeqRef.current) return;
      sessionRef.current = newPlacesSessionToken();
      if (!details.lat || !details.lng) throw new Error('Could not verify that address');
      const place: VerifiedPlace = {
        formatted: details.formatted,
        address: details.address,
        city: details.city || 'Kingman',
        state: details.state || 'AZ',
        zipCode: details.zipCode || '',
        lat: details.lat,
        lng: details.lng,
      };
      setAddress(details.formatted || pred.description);
      await continueWithPlace(place, seq);
    } catch (err: any) {
      if (seq !== checkSeqRef.current) return;
      clearLeadContext();
      setStatus('error');
      setMessage(err?.message || 'Could not verify that address. Pick another suggestion.');
    }
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (status === 'checking') return;

    const q = address.trim();
    if (q.length < 3) {
      setStatus('error');
      setMessage('Start typing your street address, then pick it from the Google suggestions.');
      return;
    }
    setStatus('checking');
    setMessage('Looking up address…');
    try {
      const preds = await placeAutocomplete(q, sessionRef.current);
      setStatus('idle');
      setMessage('');
      if (!preds.length) {
        setStatus('error');
        setMessage('No matching Google addresses found. Try a fuller street address.');
        return;
      }
      setSuggestions(preds);
      setListOpen(true);
      setStatus('error');
      setMessage('Select your address from the Google suggestions below to continue.');
    } catch (err: any) {
      setStatus('error');
      setMessage(err?.message || 'Address suggestions unavailable. Please try again.');
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!listOpen || !suggestions.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => (h + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => (h <= 0 ? suggestions.length - 1 : h - 1));
    } else if (e.key === 'Enter' && highlight >= 0) {
      e.preventDefault();
      void pickSuggestion(suggestions[highlight]);
    } else if (e.key === 'Escape') {
      setListOpen(false);
    }
  };

  const showSuggestions = status !== 'checking' && listOpen && (suggestions.length > 0 || loadingSuggest);

  return (
    <div className="fixed inset-0 z-[99999] flex items-end sm:items-center justify-center p-0 sm:p-4">
      <button
        type="button"
        className="absolute inset-0 bg-black/60"
        aria-label="Close"
        onClick={() => {
          bumpCheck();
          onClose();
        }}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="plan-connect-title"
        className="relative w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl bg-white shadow-2xl flex flex-col max-h-[92vh] sm:max-h-[85vh]"
      >
        <div className="flex-shrink-0 px-5 pt-5 pb-3 sm:px-6 sm:pt-6 border-b border-gray-100">
          <button
            type="button"
            onClick={() => {
              bumpCheck();
              onClose();
            }}
            className="absolute top-3 right-3 text-gray-400 hover:text-gray-700 text-2xl leading-none px-2"
            aria-label="Close"
          >
            ×
          </button>
          <h3 id="plan-connect-title" className="text-xl font-bold text-gray-900 pr-8">
            Enter your service address
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            You selected <strong>{plan.name}</strong>
            {plan.speed ? ` · ${plan.speed}` : ''}. Pick your address from Google to continue.
          </p>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 sm:px-6">
          {status !== 'checking' && (
            <form onSubmit={onSubmit}>
              <div ref={wrapRef}>
                <input
                  type="text"
                  value={address}
                  onChange={(e) => {
                    bumpCheck();
                    setAddress(e.target.value);
                    if (status !== 'idle') {
                      setStatus('idle');
                      setMessage('');
                    }
                  }}
                  onFocus={() => {
                    if (suggestions.length) setListOpen(true);
                  }}
                  onKeyDown={onKeyDown}
                  placeholder="Start typing your street address…"
                  className="w-full rounded-xl border border-gray-300 px-4 py-3 text-gray-900 text-base focus:outline-none focus:ring-2 focus:ring-airmax-blue focus:border-transparent"
                  autoComplete="off"
                  autoFocus
                />

                {/* In-flow list (not absolute) so suggestions are never clipped */}
                {showSuggestions && (
                  <ul className="mt-2 max-h-[min(50vh,280px)] overflow-y-auto rounded-xl border border-gray-200 bg-white shadow-sm divide-y divide-gray-100">
                    {loadingSuggest && !suggestions.length && (
                      <li className="px-4 py-3 text-sm text-gray-500">Searching Google…</li>
                    )}
                    {suggestions.map((pred, idx) => (
                      <li key={pred.placeId}>
                        <button
                          type="button"
                          className={`w-full text-left px-4 py-3 text-sm ${
                            idx === highlight ? 'bg-blue-50' : 'hover:bg-gray-50'
                          }`}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => void pickSuggestion(pred)}
                        >
                          <div className="font-semibold text-gray-900">{pred.mainText}</div>
                          {pred.secondaryText && (
                            <div className="text-gray-500 text-xs mt-0.5">{pred.secondaryText}</div>
                          )}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <button
                type="submit"
                className="mt-3 w-full rounded-xl bg-airmax-blue hover:bg-blue-700 text-white font-bold px-6 py-3 transition-colors"
              >
                Continue
              </button>
            </form>
          )}

        {status === 'checking' && (
          <CoverageCheckAnimation active label="Checking your address…" />
        )}

          {message && status !== 'checking' && (
            <div
              className={`mt-3 rounded-xl px-4 py-3 text-sm font-medium ${
                status === 'unavailable'
                  ? 'bg-orange-50 text-orange-900 border border-orange-200'
                  : status === 'error'
                    ? 'bg-red-50 text-red-800 border border-red-200'
                    : 'bg-blue-50 text-blue-900 border border-blue-200'
              }`}
            >
              {message}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PlanConnectModal;
